import { useEffect, useState, useRef, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ProfileImage from "./ProfileImage";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store/configureStore";
import { setWorkerProfileImage } from "store/slices/workerDetails";
import "../ChangeDialogs.css";
import { toastError, toastSuccess } from "components/custom-toasts";
import ModalHeaderWithActions from "pages/UI/ModalHeaderWithActions";

interface Props {
    currentPhotoSrc: string | undefined;
    show: boolean;
    onClose: () => void;
    onSubmit: () => void;
}

const ChangePhoto = (props: Props) => {
    const { currentPhotoSrc, show, onClose } = props;
    const { t } = useTranslation("parts");
    const dispatch = useDispatch<AppDispatch>();
    const inputImageFile = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewSrc, setPreviewSrc] = useState<string>(currentPhotoSrc || "");

    useEffect(() => {
        if (!selectedFile) {
            setPreviewSrc(currentPhotoSrc || "");
            return;
        }

        const objectUrl = URL.createObjectURL(selectedFile);
        setPreviewSrc(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [selectedFile, currentPhotoSrc]);

    useEffect(() => {
        if (!show) {
            setSelectedFile(null);
        }
    }, [show]);

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setSelectedFile(e.target.files[0]);
            e.target.value = "";
        }
    };

    const handleSubmit = useCallback(async () => {
        if (!selectedFile) {
            toastError({ message: t(`common:postError`) });
            return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(selectedFile);
        reader.onloadend = () => {
            const newImageByteString = reader.result as string;
            if (newImageByteString) {
                dispatch(setWorkerProfileImage(newImageByteString));
                toastSuccess({ message: t(`common:posted`) });
                props.onSubmit();
            }
        };
        reader.onerror = () => {
            toastError({ message: t(`common:postError`) });
        };
    }, [dispatch, props, selectedFile, t]);

    return (
        <Modal show={show} onHide={onClose} className="change-modal">
            <ModalHeaderWithActions
                title={t("parts:changeDialog.photo.changePhoto")}
                onClose={onClose}
                canSubmit={true}
                onSubmit={handleSubmit}
            />
            <Modal.Body className="d-flex flex-column align-items-center gap-2">
                <div className="mb-4">
                    <ProfileImage className='big' src={previewSrc} />
                </div>
                <div className="mb-3">
                    <div className="input-content text-center">
                        <input
                            type="file"
                            ref={inputImageFile}
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={handlePhotoChange}
                        />
                        <Button
                            className="visual-selection-aid"
                            variant="outline-active"
                            onClick={e => {
                                e.preventDefault();
                                if (inputImageFile.current === null) return;
                                inputImageFile.current.click();
                            }}
                        >
                            {t("parts:changeDialog.photo.uploadPhoto")}
                        </Button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ChangePhoto;
